import { useMemo, useState } from 'react'
import { Eye, Loader2, Search, X } from 'lucide-react'
import { toast } from 'sonner'
import {
  startViewingAs,
  stopViewingAs,
  useAppDispatch,
  useAppSelector,
  useGetAuthzMeQuery,
  viewAsUserSelector
} from '@axilon-apps/domains-ontology-ui-state'
import { useGetUsersQuery } from '@axilon-apps/domains-identity/state'

import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'

/** Global-admin launcher for the proxy's read-only view-as mode. */
export function ViewAsControl() {
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [starting, setStarting] = useState(false)
  const target = useAppSelector(viewAsUserSelector)
  const { data: users } = useGetUsersQuery({ page: 1, perPage: 100, enabled: true })
  const { refetch } = useGetAuthzMeQuery()

  const matchingUsers = useMemo(() => {
    const query = search.trim().toLocaleLowerCase()
    return (users?.items ?? []).filter((user) => {
      if (!query) return true
      return `${user.name} ${user.username}`.toLocaleLowerCase().includes(query)
    })
  }, [search, users])

  const start = async (user: { id: string; name: string; username: string }) => {
    setStarting(true)
    dispatch(startViewingAs(user))
    try {
      const result = await refetch()
      const echoedUserId = result.data?.viewAs?.userId
      if (echoedUserId !== user.id) {
        dispatch(stopViewingAs())
        toast.error('View as could not be confirmed for this user.')
        return
      }
      setOpen(false)
      setSearch('')
    } catch {
      dispatch(stopViewingAs())
      toast.error('Could not start the view-as preview.')
    } finally {
      setStarting(false)
    }
  }

  if (target) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
          <Eye className="size-3.5" />
          View as
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3">
        <div className="mb-3">
          <p className="text-sm font-medium text-ink">View as user</p>
          <p className="mt-0.5 text-xs text-ink-3">Preview the application with their access.</p>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-ink-3" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search active users"
            className="h-9 pl-8"
          />
        </div>
        <div className="mt-2 max-h-64 overflow-y-auto">
          {matchingUsers.map((user) => (
            <button
              key={user.id}
              type="button"
              disabled={starting}
              onClick={() => void start(user)}
              className="flex w-full flex-col rounded-md px-2.5 py-2 text-left transition-colors hover:bg-muted disabled:cursor-wait disabled:opacity-50"
            >
              <span className="text-sm font-medium text-ink">{user.name || user.username}</span>
              {user.name && <span className="text-xs text-ink-3">{user.username}</span>}
            </button>
          ))}
          {!matchingUsers.length && (
            <p className="px-2.5 py-5 text-center text-sm text-ink-3">No active users found.</p>
          )}
        </div>
        {starting && (
          <div className="mt-2 flex items-center gap-2 text-xs text-ink-3">
            <Loader2 className="size-3.5 animate-spin" /> Starting preview…
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

export function ViewAsBanner() {
  const dispatch = useAppDispatch()
  const target = useAppSelector(viewAsUserSelector)
  const { refetch } = useGetAuthzMeQuery()

  if (!target) return null

  const exit = () => {
    dispatch(stopViewingAs())
    void refetch()
  }

  return (
    <div className="flex min-h-9 items-center justify-center gap-2 border-b border-amber-300 bg-amber-50 px-4 text-xs text-amber-950 dark:border-amber-700 dark:bg-amber-950/35 dark:text-amber-100">
      <Eye className="size-3.5" />
      <span>
        Viewing as <strong>{target.name || target.username}</strong> — read-only preview
      </span>
      <Button variant="ghost" size="sm" onClick={exit} className="h-6 gap-1 px-1.5 text-xs">
        <X className="size-3.5" /> Exit
      </Button>
    </div>
  )
}
